import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Group, NumberInput, Text, Textarea, TextInput } from "@mantine/core";
import type { Setting } from "../api/types";
import { MONO, ResetChip, SettingShell, useSaveSetting } from "./SettingShell";
import { parse, render, toBytes, toUnit, type Field } from "./settingsFields";
import { Chip } from "../ui/Chip";
import { useNativeResource } from "../ui/Artwork";

/**
 * Everything you type: numbers, byte counts, URLs, paths, free text and the two policy
 * headers that accept any string.
 *
 * One component rather than seven, because the contract is the same for all of them -- local
 * draft, Enter or the Save chip commits, Escape reverts -- and only the element differs.
 */
export function TypedRow({ setting, field }: { setting: Setting; field: Field }) {
  const { save, error } = useSaveSetting(setting.key);
  const { t } = useTranslation("admin");
  const stored = storedDraft(setting, field);
  const [draft, setDraft] = useState({ base: stored, value: stored });
  const changed = draft.value !== draft.base;
  const dirty = changed && draft.value !== stored;
  const conflict = dirty && draft.base !== stored;

  useEffect(() => {
    setDraft((current) =>
      current.value === current.base || current.value === stored
        ? { base: stored, value: stored }
        : current,
    );
  }, [stored]);

  const commit = () => {
    if (conflict) return;
    const value = draft.value;
    void save(committed(setting, field, value)).then((saved) => {
      if (saved)
        setDraft((current) => (current.value === value ? { base: value, value } : current));
    });
  };
  const keys = (event: { key: string }) => {
    if (event.key === "Enter") commit();
    if (event.key === "Escape") setDraft({ base: stored, value: stored });
  };

  return (
    <SettingShell
      setting={setting}
      error={error}
      actions={
        <>
          {dirty && (
            <Chip variant="accent" disabled={conflict} onClick={commit}>
              {t("common.save")}
            </Chip>
          )}
          {conflict && (
            <>
              <Chip onClick={() => setDraft({ base: stored, value: stored })}>
                {t("inspector.useServerVersion")}
              </Chip>
              <Chip
                variant="accent"
                onClick={() => setDraft((current) => ({ ...current, base: stored }))}
              >
                {t("inspector.keepDraft")}
              </Chip>
            </>
          )}
          <ResetChip
            setting={setting}
            onReset={(fresh) => {
              const value = storedDraft(fresh, field);
              setDraft({ base: value, value });
            }}
          />
        </>
      }
    >
      <TypedInput
        setting={setting}
        field={field}
        draft={draft.value}
        onDraft={(value) => setDraft((current) => ({ ...current, value }))}
        onKeys={keys}
      />
      {conflict && (
        <Text size="xs" c="warn" mt={4}>
          {t("inspector.serverChanged")}
        </Text>
      )}
    </SettingShell>
  );
}

function TypedInput({
  setting,
  field,
  draft,
  onDraft,
  onKeys,
}: {
  setting: Setting;
  field: Field;
  draft: string | number;
  onDraft: (next: string | number) => void;
  onKeys: (event: { key: string }) => void;
}) {
  const { t } = useTranslation("admin");
  const id = `setting-${setting.key}`;
  const cover = field.kind === "artwork" ? Number(setting.value) || 0 : 0;
  const artworkUrl = useNativeResource(cover > 0 ? `/api/artwork/${cover}` : null);

  switch (field.kind) {
    case "number":
    case "bytes":
    case "artwork": {
      // The preview URL is acquired above so this switch never calls a hook conditionally.
      const input = (
        <NumberInput
          id={id}
          value={draft}
          // The value arrives typed rather than as an event, and an emptied field arrives as
          // "". Coercing it here would refill the box the moment it was cleared.
          onChange={onDraft}
          min={field.min}
          max={field.max}
          step={field.step}
          suffix={field.unit ? ` ${field.unit}` : field.suffix}
          // A byte box is typed in whole GiB or MiB and stored in bytes, so it needs the
          // fraction; every other spinner takes its precision from the value it holds.
          allowDecimal={field.kind === "bytes" || !Number.isInteger(setting.value)}
          decimalScale={field.kind === "bytes" ? 2 : undefined}
          // Honest now that the bounds are the setting's real domain -- a port is 1..65535,
          // a count is at least one -- rather than a range invented on the client.
          clampBehavior="strict"
          onKeyDown={onKeys}
        />
      );
      if (field.kind !== "artwork") return input;
      return (
        <Group gap={10} wrap="nowrap" align="center">
          {input}
          {artworkUrl && (
            <img
              src={artworkUrl}
              alt={t("settingsPage.coverAlt", { id: cover })}
              width={40}
              height={40}
              style={{ objectFit: "cover", borderRadius: "var(--mantine-radius-sm)" }}
            />
          )}
        </Group>
      );
    }
    case "textarea":
      return (
        <Textarea
          id={id}
          autosize
          minRows={3}
          spellCheck={false}
          value={String(draft)}
          // Monospace and a size down, so a stray space inside a policy is visible. It goes
          // on the input itself, which is the element the text lives in.
          styles={{ input: { fontFamily: MONO, fontSize: 12 } }}
          onChange={(event) => onDraft(event.target.value)}
          // No save on Enter, unlike the single-line rows: a multi-line value has to be
          // typable.
          onKeyDown={(event) => {
            if (event.key === "Escape") onKeys(event);
          }}
        />
      );
    case "autocomplete":
      return (
        <Autocomplete
          id={id}
          spellCheck={false}
          value={String(draft)}
          data={field.suggestions ? [...field.suggestions] : []}
          onChange={onDraft}
          onKeyDown={onKeys}
        />
      );
    default:
      return (
        <TextInput
          id={id}
          type={field.kind === "url" ? "url" : undefined}
          spellCheck={false}
          value={String(draft)}
          styles={field.kind === "path" ? { input: { fontFamily: MONO } } : undefined}
          onChange={(event) => onDraft(event.target.value)}
          onKeyDown={onKeys}
        />
      );
  }
}

/** What the box shows for a stored value: byte counts in their own unit, everything else raw. */
function storedDraft(setting: Setting, field: Field): string | number {
  if (field.kind === "bytes") return toUnit(Number(setting.value), field.unit ?? "MiB");
  if (field.kind === "number" || field.kind === "artwork") return Number(setting.value);
  return render(setting.value);
}

/** What a save sends, shared by Enter and the Save chip so the two never disagree. */
function committed(setting: Setting, field: Field, draft: string | number): unknown {
  if (field.kind === "bytes") {
    // An emptied box has nothing to send, so it keeps what is stored.
    return typeof draft === "number" ? toBytes(draft, field.unit ?? "MiB") : setting.value;
  }
  if (field.kind === "number" || field.kind === "artwork") {
    return typeof draft === "number" ? draft : setting.value;
  }
  return parse(String(draft), setting.value);
}
